import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileX2, Loader2, Mail, Phone, ShieldCheck, UploadCloud } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { BUSINESS, SERVICES } from "@/lib/site";
import usePageTitle from "@/lib/usePageTitle";

const CONTACT_METHODS = ["Email", "Phone call", "Text message"];
const SERVICE_SLUGS = SERVICES.map((s) => s.slug).concat("not-sure-yet");

const NEXT_STEPS = [
  { t: "A real person reads it", d: "Your request goes straight to our inbox — no bots, no auto-replies." },
  { t: "You get a personal quote", d: "We reply using your preferred contact method. We don't auto-calculate prices or turnaround times." },
  { t: "Approve and we start", d: "Happy with the quote? We get to work in our own workshop." },
];

export default function Quote() {
  usePageTitle("Request a Quote — Skies The Limit Creations LLC");
  const [params] = useSearchParams();
  const pre = params.get("s");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    contact_method: "Email",
    service: SERVICE_SLUGS.includes(pre) ? pre : "",
    quantity: "",
    desired_date: "",
    description: "",
  });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [ref, setRef] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const fileInput = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.service || !form.description.trim()) {
      setErrMsg("Please fill in your name, email, service, and description.");
      setStatus("error");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("artwork", file);
    setStatus("sending");
    setErrMsg("");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setRef(d.reference_id || "");
        setStatus("success");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const detail = Array.isArray(d.detail) ? d.detail[0]?.msg : d.detail;
        setErrMsg(typeof detail === "string" ? detail : "Something went wrong — please try again or email us directly.");
        setStatus("error");
      }
    } catch {
      setErrMsg("We couldn't reach the server — please try again, or email us directly.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="grain flex min-h-screen items-center justify-center px-5 pb-20 pt-[120px]">
        <Reveal>
          <div className="card-panel mx-auto max-w-xl p-10 text-center" data-testid="quote-success-panel">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" aria-hidden />
            <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white">Request received!</h1>
            <p className="mt-4 text-sm leading-relaxed text-mist">
              Your reference number is <span className="font-mono font-semibold text-gold">{ref}</span>.
              A confirmation email is on its way to {form.email}. We'll review your request and reply by{" "}
              {form.contact_method.toLowerCase()} — a real person, no automatic prices.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/" data-testid="quote-success-home-button" className="btn-ghost !px-6 !py-3 text-xs">
                Back to Home
              </Link>
              <a href={`mailto:${BUSINESS.email}`} data-testid="quote-success-email-button" className="btn-primary !px-6 !py-3 text-xs">
                Email Us <Mail className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="grain">
      <header className="relative overflow-hidden pb-8 pt-[130px] lg:pt-[160px]">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="quote-header">
          <Reveal>
            <p className="eyebrow mb-5">Request a quote</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Tell us what to make.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
              Short form, friendly humans. Sketches and rough ideas are welcome — artwork is optional.
            </p>
          </Reveal>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-12 px-5 pb-24 sm:px-8 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16 lg:pb-32">
        <Reveal>
          <form onSubmit={onSubmit} className="card-panel space-y-7 p-6 sm:p-10" data-testid="quote-form" noValidate>
            <div className="grid gap-7 sm:grid-cols-2">
              <div>
                <label htmlFor="q-name" className="mb-2 block text-sm font-semibold text-white">
                  Name <span className="text-ember">*</span>
                </label>
                <input
                  id="q-name"
                  type="text"
                  autoComplete="name"
                  className="field"
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                  data-testid="quote-name-input"
                />
              </div>
              <div>
                <label htmlFor="q-email" className="mb-2 block text-sm font-semibold text-white">
                  Email <span className="text-ember">*</span>
                </label>
                <input
                  id="q-email"
                  type="email"
                  autoComplete="email"
                  className="field"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="quote-email-input"
                />
              </div>
              <div>
                <label htmlFor="q-phone" className="mb-2 block text-sm font-semibold text-white">
                  Phone <span className="font-normal text-mist">(optional)</span>
                </label>
                <input
                  id="q-phone"
                  type="tel"
                  autoComplete="tel"
                  className="field"
                  placeholder="For calls or texts"
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="quote-phone-input"
                />
              </div>
              <div>
                <span className="mb-2 block text-sm font-semibold text-white">Preferred contact method</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Preferred contact method">
                  {CONTACT_METHODS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
                      aria-pressed={form.contact_method === m}
                      data-testid={`quote-contact-method-${m.toLowerCase().replace(/\s+/g, "-")}-option`}
                      className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-200 ${
                        form.contact_method === m
                          ? "border-gold/60 bg-gold/10 text-gold"
                          : "border-white/15 text-mist hover:border-white/40 hover:text-white"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-7 sm:grid-cols-2">
              <div>
                <label htmlFor="q-service" className="mb-2 block text-sm font-semibold text-white">
                  Service or product <span className="text-ember">*</span>
                </label>
                <select id="q-service" className="field appearance-none" value={form.service} onChange={set("service")} data-testid="quote-service-select">
                  <option value="" disabled>
                    Choose a service…
                  </option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name}
                    </option>
                  ))}
                  <option value="not-sure-yet">Not sure yet</option>
                </select>
              </div>
              <div>
                <label htmlFor="q-quantity" className="mb-2 block text-sm font-semibold text-white">
                  Quantity <span className="font-normal text-mist">(rough is fine)</span>
                </label>
                <input
                  id="q-quantity"
                  type="text"
                  inputMode="numeric"
                  className="field"
                  placeholder='e.g. "12" or "25–50"'
                  value={form.quantity}
                  onChange={set("quantity")}
                  data-testid="quote-quantity-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="q-date" className="mb-2 block text-sm font-semibold text-white">
                  Desired date <span className="font-normal text-mist">(optional — we'll confirm what's possible)</span>
                </label>
                <input
                  id="q-date"
                  type="date"
                  className="field [color-scheme:dark]"
                  value={form.desired_date}
                  onChange={set("desired_date")}
                  data-testid="quote-desired-date-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="q-desc" className="mb-2 block text-sm font-semibold text-white">
                Describe it <span className="text-ember">*</span>
              </label>
              <textarea
                id="q-desc"
                rows={5}
                className="field resize-y"
                placeholder="What is it, who's it for, colors, sizes, text to include — whatever you know so far."
                value={form.description}
                onChange={set("description")}
                data-testid="quote-description-textarea"
              />
            </div>

            <div>
              <span className="mb-2 block text-sm font-semibold text-white">Artwork or reference image</span>
              <input
                ref={fileInput}
                id="q-artwork"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.pdf,.svg,.zip"
                className="sr-only"
                onChange={onFile}
                data-testid="quote-artwork-upload-input"
              />
              {!file ? (
                <label
                  htmlFor="q-artwork"
                  data-testid="quote-artwork-upload-button"
                  className="flex cursor-pointer items-center justify-center gap-3 rounded-xl border border-dashed border-white/20 bg-panel/60 px-4 py-6 text-sm text-mist transition-colors duration-200 hover:border-gold/50 hover:text-white"
                >
                  <UploadCloud className="h-5 w-5 text-gold" aria-hidden />
                  Attach a file (optional) — up to 8 MB
                </label>
              ) : (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-white/15 bg-panel px-4 py-3" data-testid="quote-artwork-file-chip">
                  <span className="truncate text-sm text-white">{file.name}</span>
                  <button
                    type="button"
                    onClick={clearFile}
                    aria-label="Remove attached file"
                    data-testid="quote-artwork-remove-button"
                    className="rounded-full p-1.5 text-mist transition-colors duration-200 hover:bg-white/10 hover:text-white"
                  >
                    <FileX2 className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>

            {status === "error" && (
              <p className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="quote-error-alert">
                {errMsg}
              </p>
            )}

            <button type="submit" className="btn-primary w-full sm:w-auto" disabled={status === "sending"} data-testid="quote-submit-button">
              {status === "sending" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Sending…
                </>
              ) : (
                <>
                  Send My Request <ArrowRight className="h-4 w-4" aria-hidden />
                </>
              )}
            </button>
            <p className="text-xs leading-relaxed text-mist/70">
              Submitting sends your request straight to {BUSINESS.email}. We'll reply personally — quotes are always
              reviewed by a human, never generated automatically.
            </p>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <aside className="space-y-5">
            <div className="card-panel p-7" data-testid="quote-next-steps">
              <p className="eyebrow mb-6">What happens next</p>
              <ol className="space-y-6">
                {NEXT_STEPS.map((s, i) => (
                  <li key={s.t} className="flex gap-4">
                    <span className="mt-0.5 font-mono text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h2 className="font-display text-base font-bold text-white">{s.t}</h2>
                      <p className="mt-1 text-sm leading-relaxed text-mist">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="card-panel p-7" data-testid="quote-honesty-note">
              <ShieldCheck className="h-6 w-6 text-gold" aria-hidden />
              <p className="mt-4 text-sm leading-relaxed text-mist">
                We never promise an automatic price or turnaround time — every quote is reviewed by hand so we can be
                honest about what's possible.
              </p>
            </div>
            <div className="card-panel p-7" data-testid="quote-contact-card">
              <p className="eyebrow mb-4">Rather talk it out?</p>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 text-sm text-mist transition-colors duration-200 hover:text-white" data-testid="quote-direct-email-link">
                <Mail className="h-4 w-4 text-gold" aria-hidden /> <span className="break-all">{BUSINESS.email}</span>
              </a>
              <a href={BUSINESS.phoneHref} className="mt-3 flex items-center gap-3 text-sm text-mist transition-colors duration-200 hover:text-white" data-testid="quote-direct-phone-link">
                <Phone className="h-4 w-4 text-gold" aria-hidden /> {BUSINESS.phoneDisplay}
              </a>
            </div>
          </aside>
        </Reveal>
      </main>
    </div>
  );
}
